import { useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, RefreshCw, Pause, Play, Search, ScrollText } from 'lucide-react'
import clsx from 'clsx'
import { managedDeviceLogsApi } from '../../services/managedDeviceLogsApi'
import { formatDateTimeSeconds } from '../../utils/format'

// Severidade syslog (RFC 5424). Cores semânticas (error/warning) — não são cor de marca.
const SEVERITIES = [
  { value: 0, label: 'Emergência', badge: 'bg-red-100 text-red-800' },
  { value: 1, label: 'Alerta', badge: 'bg-red-100 text-red-800' },
  { value: 2, label: 'Crítico', badge: 'bg-red-100 text-red-800' },
  { value: 3, label: 'Erro', badge: 'bg-red-50 text-red-700' },
  { value: 4, label: 'Aviso', badge: 'bg-amber-50 text-amber-700' },
  { value: 5, label: 'Notice', badge: 'bg-gray-100 text-gray-700' },
  { value: 6, label: 'Info', badge: 'bg-gray-100 text-gray-600' },
  { value: 7, label: 'Debug', badge: 'bg-gray-50 text-gray-500' },
]

const PERIODS = [
  { value: '15m', label: 'Últimos 15 min', ms: 15 * 60 * 1000 },
  { value: '1h', label: 'Última hora', ms: 60 * 60 * 1000 },
  { value: '6h', label: 'Últimas 6 horas', ms: 6 * 60 * 60 * 1000 },
  { value: '24h', label: 'Últimas 24 horas', ms: 24 * 60 * 60 * 1000 },
  { value: '7d', label: 'Últimos 7 dias', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: 'all', label: 'Tudo (retenção)', ms: null },
]

const SEVERITY_FILTERS = [
  { value: '', label: 'Todas as severidades' },
  { value: '3', label: 'Erro ou pior' },
  { value: '4', label: 'Aviso ou pior' },
  { value: '6', label: 'Info ou pior (sem debug)' },
]

const PAGE_SIZE = 200
const LIVE_INTERVAL_MS = 5000

function severityMeta(value) {
  return SEVERITIES.find(s => s.value === value) || SEVERITIES[6]
}

/**
 * Visualizador de logs de ManagedDevice (syslog remoto via VPN).
 * - deviceId: logs de um device (aba do detalhe).
 * - tenantId: logs do tenant inteiro; `devices` habilita o filtro por device e a coluna Device.
 */
export default function DeviceLogViewer({ deviceId = null, tenantId = null, devices = null }) {
  const { t } = useTranslation()
  const [period, setPeriod] = useState('1h')
  const [maxSeverity, setMaxSeverity] = useState('')
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [deviceFilter, setDeviceFilter] = useState('')
  const [live, setLive] = useState(true)
  const [expandedId, setExpandedId] = useState(null)

  const showDeviceColumn = !deviceId
  const filters = useMemo(
    () => ({ period, maxSeverity, search, deviceFilter }),
    [period, maxSeverity, search, deviceFilter]
  )

  const query = useInfiniteQuery({
    queryKey: ['managedDeviceLogs', deviceId, tenantId, filters],
    enabled: !!(deviceId || tenantId),
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const periodMs = PERIODS.find(p => p.value === filters.period)?.ms
      const params = { limit: PAGE_SIZE }
      if (periodMs) params.from = new Date(Date.now() - periodMs).toISOString()
      if (filters.maxSeverity !== '') params.maxSeverity = filters.maxSeverity
      if (filters.search) params.search = filters.search
      if (pageParam) params.beforeId = pageParam
      if (deviceId) return managedDeviceLogsApi.getByDevice(deviceId, params)
      if (filters.deviceFilter) params.managedDeviceId = filters.deviceFilter
      return managedDeviceLogsApi.getByTenant(tenantId, params)
    },
    getNextPageParam: (lastPage) => lastPage?.nextBeforeId ?? undefined,
    refetchInterval: () =>
      live && !(typeof document !== 'undefined' && document.hidden) ? LIVE_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  })

  const items = useMemo(
    () => (query.data?.pages || []).flatMap(p => p?.items || []),
    [query.data]
  )

  const applySearch = (e) => {
    e.preventDefault()
    setSearch(searchDraft.trim())
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Filtros */}
      <div className="p-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
        {showDeviceColumn && devices && (
          <select
            className="input text-sm w-auto"
            value={deviceFilter}
            onChange={e => setDeviceFilter(e.target.value)}
          >
            <option value="">{t('deviceLogs.allDevices', 'Todos os dispositivos')}</option>
            {devices.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}
        <select className="input text-sm w-auto" value={period} onChange={e => setPeriod(e.target.value)}>
          {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select className="input text-sm w-auto" value={maxSeverity} onChange={e => setMaxSeverity(e.target.value)}>
          {SEVERITY_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <form onSubmit={applySearch} className="flex items-center gap-1 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              className="input text-sm pl-8 w-full"
              placeholder={t('deviceLogs.searchPlaceholder', 'Buscar na mensagem ou tópicos (ex.: logged in, account)')}
              value={searchDraft}
              onChange={e => setSearchDraft(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-secondary text-sm">{t('common.buttons.search', 'Buscar')}</button>
        </form>
        <button
          type="button"
          onClick={() => setLive(v => !v)}
          className="btn btn-secondary text-sm inline-flex items-center gap-1"
          title={live ? 'Pausar atualização automática' : 'Retomar atualização automática'}
        >
          {live ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {live ? t('deviceLogs.live', 'Ao vivo') : t('deviceLogs.paused', 'Pausado')}
        </button>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="btn btn-secondary text-sm"
          title="Atualizar"
        >
          <RefreshCw className={clsx('w-4 h-4', query.isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Conteúdo */}
      {query.isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          {t('deviceLogs.loading', 'Carregando logs...')}
        </div>
      ) : query.isError ? (
        <div className="p-6 text-sm text-red-700">
          {t('deviceLogs.error', 'Não foi possível carregar os logs.')}
        </div>
      ) : items.length === 0 ? (
        <div className="py-12 px-6 text-center text-sm text-gray-500">
          <ScrollText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>{t('deviceLogs.empty', 'Nenhum log no período/filtro selecionado.')}</p>
          <p className="mt-1 text-xs text-gray-400">
            {t(
              'deviceLogs.emptyHint',
              'MikroTiks passam a enviar logs após o bootstrap ou a reconciliação automática (até ~15 min).'
            )}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-3 py-2 whitespace-nowrap">{t('deviceLogs.col.time', 'Data/hora')}</th>
                {showDeviceColumn && (
                  <th className="text-left font-medium px-3 py-2">{t('deviceLogs.col.device', 'Dispositivo')}</th>
                )}
                <th className="text-left font-medium px-3 py-2">{t('deviceLogs.col.severity', 'Severidade')}</th>
                <th className="text-left font-medium px-3 py-2">{t('deviceLogs.col.topics', 'Tópicos')}</th>
                <th className="text-left font-medium px-3 py-2 w-full">{t('deviceLogs.col.message', 'Mensagem')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(log => {
                const sev = severityMeta(log.severity)
                const expanded = expandedId === log.id
                return (
                  <tr
                    key={log.id}
                    onClick={() => setExpandedId(expanded ? null : log.id)}
                    className="hover:bg-gray-50 cursor-pointer align-top"
                  >
                    <td className="px-3 py-1.5 font-mono text-gray-600 whitespace-nowrap">
                      {formatDateTimeSeconds(log.receivedAt)}
                    </td>
                    {showDeviceColumn && (
                      <td className="px-3 py-1.5 text-gray-800 whitespace-nowrap">{log.deviceName || '—'}</td>
                    )}
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span className={clsx('inline-block rounded px-1.5 py-0.5 font-medium', sev.badge)}>
                        {sev.label}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-gray-500 whitespace-nowrap">{log.topics || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-900">
                      <div className={clsx(expanded ? 'whitespace-pre-wrap break-all' : 'truncate max-w-[60ch] md:max-w-[90ch]')}>
                        {log.message}
                      </div>
                      {expanded && (
                        <div className="mt-1 text-[11px] text-gray-400">
                          {log.hostname ? `host: ${log.hostname} · ` : ''}facility: {log.facility} · {log.source} · #{log.id}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="p-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>{items.length} {t('deviceLogs.lines', 'linhas')}</span>
            {query.hasNextPage && (
              <button
                type="button"
                className="btn btn-secondary text-xs"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage
                  ? t('deviceLogs.loadingMore', 'Carregando...')
                  : t('deviceLogs.loadMore', 'Carregar mais antigos')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
